import { useState, useEffect } from 'react';
import GeneralModal from '../modal/GeneralModal';
import { Trans, useTranslation } from 'react-i18next';
import {
  ResetUniversalStaffLoginModalFormData,
  UniversalStaffLoginModalType,
  VENUE_UNIT_DETAILS_KEY,
} from './type';
import Button from '../button/button';
import Input from '../Input/input';
import { UserService } from '../../service/userService';
import { CreateUserResponse, UserType } from '@inplay/models';
import { toast } from 'react-toastify';
import ShowEye from '../../assets/show_eye.png';
import CloseEye from '../../assets/close_eye.png';
import { AuthService } from '../../service/authService';
import { EmailSent } from '../../pages/onboarding/venuSetup/type';
import ConfirmationModal from '../confirmationModal/confirmationModal';
import VenueBranchDropdown from '../venueBranchDropdown/venueBranchDropdown';
import { VenueBranchSelection } from '../../hooks/useVenueBranchAccess';

const ResetUniversalStaff = ({
  modalType,
  email,
  onClose,
  selectedVenueBranch,
  setSelectedVenueBranch,
}: {
  modalType?: UniversalStaffLoginModalType;
  email: string;
  onClose: () => void;
  selectedVenueBranch: VenueBranchSelection;
  setSelectedVenueBranch: (selectedBranch: VenueBranchSelection) => void;
}) => {
  const [formData, setFormData] =
    useState<ResetUniversalStaffLoginModalFormData>({
      email: email,
      password: '',
      venueBranchId: selectedVenueBranch?.value,
      verificationCode: '',
      confirmPassword: '',
    });
  const [isEmailSent, setIsEmailSent] = useState<boolean>(false);
  const [showCurrentPassword, setShowCurrentPassword] =
    useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] =
    useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { t } = useTranslation();
  const [emailSent, setEmailSent] = useState<EmailSent>({});
  const [showConfirmationModalOnSkip, setShowConfirmationModalOnSkip] =
    useState(false);
  const [showConfirmationModalOnContinue, setShowConfirmationModalOnContinue] =
    useState(false);

  const [errors, setErrors] = useState<ResetUniversalStaffLoginModalFormData>({
    email: '',
    password: '',
    venueBranchId: '',
    verificationCode: '',
    confirmPassword: '',
  });

  const handleFormDataChange: (field: string, value: string) => void = (
    field: string,
    value: string,
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  // Create user and send verification email
  const handleVerifyEmail = async () => {
    try {
      setIsLoading(true);
      const userService = new UserService();
      // Get venue ID dynamically from localStorage
      const venueUnitDetails = JSON.parse(
        localStorage.getItem(VENUE_UNIT_DETAILS_KEY) || '{}',
      );
      const venueId = venueUnitDetails?.venue?.id;

      if (!venueId) {
        throw new Error('Venue ID not found');
      }

      const response: CreateUserResponse =
        await userService.createUniversalStaffUser(
          formData?.email,
          venueId,
          formData?.venueBranchId, // venueBranchId
          UserType.VENUE_BRANCH_LOGIN, // type
        );

      setIsEmailSent(true);
      setEmailSent((prev) => ({
        ...prev,
        [formData?.venueBranchId]: [
          ...(prev[formData?.venueBranchId] || []),
          { email: formData?.email, userId: response?.userId },
        ],
      }));
      toast.success(
        t(`A verification code has been sent to ${formData?.email}.`),
      );
    } catch (error) {
      console.error('Error creating user:', error);
      toast.error(t('venueSetup.errorSendingVerificationCode'));
    } finally {
      setIsLoading(false);
    }
  };

  const onSave = async () => {
    try {
      setIsLoading(true);
      const authService = new AuthService();
      await authService.newPassword(
        formData.email,
        formData.password,
        formData.verificationCode,
      );
      onClose();
    } catch (error) {
      console.error('Error adding password:', error);
      toast.error(t('universalStaffLoginModal.addUniversalStaffLoginError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = (): void => {
    // Validate required fields
    const newErrors = { ...errors };
    let hasErrors = false;

    if (!formData.email) {
      newErrors.email = t('universalStaffLoginModal.emailIsRequired');
      hasErrors = true;
    }
    if (!formData.verificationCode) {
      newErrors.verificationCode = t(
        'universalStaffLoginModal.verificationCodeRequired',
      );
      hasErrors = true;
    }
    if (!formData.password) {
      newErrors.password = t('universalStaffLoginModal.passwordRequired');
      hasErrors = true;
    }
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = t(
        'universalStaffLoginModal.confirmPasswordRequired',
      );
      hasErrors = true;
    }
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = t(
        'universalStaffLoginModal.passwordsDoNotMatch',
      );
      hasErrors = true;
    }

    // Validate password match
    if (formData.password !== formData.confirmPassword) {
      toast.error(t('universalStaffLoginModal.passwordsDoNotMatch'));
      return;
    }
    setErrors(newErrors);
    if (!hasErrors) {
      // Check if any verification codes have been sent for other emails
      const hasOtherVerifiedEmails =
        emailSent[selectedVenueBranch?.value]?.length > 1;
      if (hasOtherVerifiedEmails) {
        setShowConfirmationModalOnContinue(true);
      } else {
        onSave();
      }
    }
  };

  const handleDeleteUser = async () => {
    setIsLoading(true);
    try {
      const userService = new UserService();

      // Get all emails that need to be deleted (all emails that have been sent verification codes)
      const emailsToDelete = emailSent[selectedVenueBranch?.value]?.length;

      if (emailsToDelete === 0) {
        toast.error(t('venueSetup.verificationCodeNotSent'));
        return;
      }

      // Delete users based on confirmation modal state
      if (showConfirmationModalOnContinue) {
        // Delete all users except current branch email
        await userService.deleteUser(
          emailSent[selectedVenueBranch?.value]
            ?.filter((item) => item.email !== formData.email)
            ?.map((item) => item.userId),
        );
      } else {
        // Delete all users
        const usersToDelete = emailSent[selectedVenueBranch?.value]?.map(
          (item) => item.userId,
        );
        await userService.deleteUser(usersToDelete);
        if (usersToDelete?.length > 1) {
          toast.success(t('universalStaffLoginModal.usersDeleted'));
        } else {
          toast.success(t('universalStaffLoginModal.userDeleted'));
        }
      }

      // Clear email sent state for all deleted users
      // Clear email sent state for current branch
      setEmailSent((prev) => {
        const updated = { ...prev };
        if (showConfirmationModalOnContinue) {
          // Keep only the current branch email if it exists and save the user
          updated[selectedVenueBranch?.value] = updated[
            selectedVenueBranch?.value
          ]?.filter((item) => item.email === formData.email);
          if (!updated[selectedVenueBranch?.value]?.length) {
            delete updated[selectedVenueBranch?.value];
          }
        } else {
          // Delete all emails for current branch
          delete updated[selectedVenueBranch?.value];
        }
        return updated;
      });
      setShowConfirmationModalOnSkip(false);
      setShowConfirmationModalOnContinue(false);

      if (showConfirmationModalOnSkip) {
        // Clear email and password for current branch
        setFormData((prev) => ({
          ...prev,
          password: '',
          verificationCode: '',
          confirmPassword: '',
        }));
        onClose();
      } else {
        onSave();
      }
    } catch (error) {
      console.error('Error deleting users:', error);
      toast.error(t('venueSetup.errorDeletingUser'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = async () => {
    // Check if any verification codes have been sent
    const hasVerifiedEmails = emailSent[selectedVenueBranch?.value]?.length;

    if (hasVerifiedEmails) {
      setShowConfirmationModalOnSkip(true);
    } else {
      // No emails verified, just move to next branch
      onClose();
    }
  };

  useEffect(() => {
    // Update formData when selectedVenueBranch changes
    setFormData((prev) => ({
      ...prev,
      venueBranchId: selectedVenueBranch?.value || '',
    }));
  }, [selectedVenueBranch]);
  return (
    <>
      <GeneralModal
        modalClassName="min-h-[38rem]"
        onClose={handleClose}
        headerTitle={
          modalType === UniversalStaffLoginModalType.ADD
            ? t('universalStaffLoginModal.addTitle')
            : t('universalStaffLoginModal.resetTitle')
        }
        headerTitleStyle="text-start font-inter leading-5"
        headerStyle="mt-4"
        body={
          <div className="flex flex-col w-full">
            <div className="text-black mb-6 max-w-2xl text-xs font-normal">
              {' '}
              {t('universalStaffLoginModal.resetEmailDescription')}
            </div>
            <div className="space-y-4 mb-4">
              <div className="flex w-full mt-2">
                <label className="w-[40%] pt-4 text-black font-helvetica text-xs font-[500] text-left">
                  {t('universalStaffLoginModal.venueBranch')}
                </label>
                <div className="w-full">
                  <VenueBranchDropdown
                    selectedBranch={selectedVenueBranch}
                    onSelect={setSelectedVenueBranch}
                    disabled={isLoading}
                  />
                </div>
              </div>
              <div className="flex w-full">
                <label className="w-[40%] pt-4 text-black font-helvetica text-xs font-[500] text-left">
                  {t('universalStaffLoginModal.email')} *
                </label>
                <div className="flex flex-col w-full">
                  <div className="flex w-full gap-2">
                    <Input
                      value={formData.email}
                      onChange={(e) =>
                        handleFormDataChange('email', e.target.value)
                      }
                      placeholder="example@email.com"
                      required
                      useDefaultStyling={false}
                      inputClassName="rounded-full bg-[#FAFAFA] px-6 py-3 w-full"
                      containerClassName="flex-1"
                      labelClassName="hidden"
                    />
                    <Button
                      className="px-6 py-3 rounded-full font-normal text-xs min-w-[6.5rem]"
                      onClick={handleVerifyEmail}
                    >
                      {isLoading
                        ? t('common.loading')
                        : t('universalStaffLoginModal.verify')}
                    </Button>
                  </div>
                  {errors.email && (
                    <div className="text-xs text-red-500 ms-5">
                      {errors.email}
                    </div>
                  )}
                </div>
              </div>
              {isEmailSent && (
                <div className="flex w-full">
                  <div className="w-[30%]"></div>
                  <div className={`text-sm text-[#0014FF]`}>
                    {t(
                      `A verification code has been sent to ${formData?.email}.`,
                    )}
                  </div>
                </div>
              )}
              {/* Verification Code Row */}
              <div className="flex w-full ">
                <label
                  className={`w-[40%] pt-4 text-black font-helvetica text-xs  font-[500] text-left ${
                    !isEmailSent ? 'text-[#808080]' : 'text-black'
                  }`}
                >
                  {t('universalStaffLoginModal.verificationCode')} *
                </label>
                <div className="flexflex-col w-full ">
                  <Input
                    value={formData?.verificationCode}
                    onChange={(e) =>
                      handleFormDataChange('verificationCode', e.target.value)
                    }
                    disabled={!isEmailSent}
                    placeholder="Enter code"
                    inputClassName="rounded-full bg-[#FAFAFA] px-6 py-3 w-full"
                    containerClassName="flex-1"
                    required
                  />
                  {errors.verificationCode && (
                    <div className="text-xs text-red-500 ms-5">
                      {errors.verificationCode}
                    </div>
                  )}
                  {/* <Button
            className="px-6 py-3 rounded-full font-normal bg-white border border-[#D8D8D8]-400 text-black text-xs"
            disabled={!isEmailSent || branch?.verificationCode.length < OTPLength || isLoading}
            onClick={handleVerifyCode}
          >
            {isLoading ? t('venueSetup.verifying') : t('venueSetup.verifyCode')}
          </Button> */}
                </div>
              </div>
              <div className="flex w-full relative">
                <label
                  className={`w-[40%] pt-4 text-black font-helvetica text-xs font-[500] text-left ${
                    !isEmailSent ? 'text-[#808080]' : 'text-black'
                  }`}
                >
                  {t('universalStaffLoginModal.createPassword')} *
                </label>
                <div className="flex flex-col w-full">
                  <div className="relative w-full">
                    <Input
                      type={showCurrentPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) =>
                        handleFormDataChange('password', e.target.value)
                      }
                      required
                      placeholder={t('universalStaffLoginModal.enterPassword')}
                      useDefaultStyling={false}
                      inputClassName="rounded-full bg-[#FAFAFA] px-6 py-3 w-full"
                      containerClassName="flex w-full"
                      disabled={!isEmailSent}
                    />
                    <div
                      className="passowrd-input-container absolute right-4 top-[52px] cursor-pointer"
                      onClick={() =>
                        setShowCurrentPassword?.(!showCurrentPassword)
                      }
                    >
                      {showCurrentPassword ? (
                        <img
                          src={ShowEye}
                          alt="Show Password"
                          className="w-5 h-4"
                        />
                      ) : (
                        <img
                          src={CloseEye}
                          alt="Hide Password"
                          className="w-5 h-3"
                        />
                      )}
                    </div>
                  </div>
                  {errors.password && (
                    <div className="text-xs text-red-500 ms-5">
                      {errors.password}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex w-full relative">
                <label
                  className={`w-[40%] pt-4 text-black font-helvetica text-xs font-[500] text-left ${
                    !isEmailSent ? 'text-[#808080]' : 'text-black'
                  }`}
                >
                  {t('universalStaffLoginModal.confirmPassword')} *
                </label>
                <div className="flex flex-col w-full">
                  <div className="relative w-full">
                    <Input
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={formData.confirmPassword}
                      onChange={(e) =>
                        handleFormDataChange('confirmPassword', e.target.value)
                      }
                      placeholder={t('universalStaffLoginModal.enterPassword')}
                      required
                      useDefaultStyling={false}
                      inputClassName="rounded-full bg-[#FAFAFA] px-6 py-3 w-full"
                      containerClassName="flex w-full"
                      disabled={!isEmailSent}
                    />
                    <div
                      className="passowrd-input-container absolute right-4 top-[52px] cursor-pointer"
                      onClick={() =>
                        setShowConfirmPassword?.(!showConfirmPassword)
                      }
                    >
                      {showConfirmPassword ? (
                        <img
                          src={ShowEye}
                          alt="Show Password"
                          className="w-5 h-4"
                        />
                      ) : (
                        <img
                          src={CloseEye}
                          alt="Hide Password"
                          className="w-5 h-3"
                        />
                      )}
                    </div>
                  </div>
                  {errors.confirmPassword && (
                    <div className="text-xs text-red-500 ms-5">
                      {errors.confirmPassword}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        }
        footer={
          <>
            <Button
              className="px-8 py-3 rounded-full font-normal text-xs bg-white text-[#A7A7A7] border border-[#D8D8D8] hover:bg-white hover:text-[#A7A7A7]  min-w-[6.5rem]"
              onClick={handleClose}
              disabled={isLoading}
            >
              {isLoading
                ? t('common.loading')
                : t('universalStaffLoginModal.cancel')}
            </Button>
            <Button
              className="px-8 py-3 rounded-full font-normal text-xs min-w-[6.5rem]"
              onClick={handleSave}
              disabled={isLoading}
            >
              {isLoading
                ? t('common.loading')
                : t('universalStaffLoginModal.save')}
            </Button>
          </>
        }
        footerStyle="justify-between flex px-0"
      />
      {(showConfirmationModalOnSkip || showConfirmationModalOnContinue) && (
        <ConfirmationModal
          isLoading={isLoading}
          showConfirmButton
          isOpen={
            showConfirmationModalOnSkip || showConfirmationModalOnContinue
          }
          onConfirm={handleDeleteUser}
          onClose={() =>
            showConfirmationModalOnSkip
              ? setShowConfirmationModalOnSkip(false)
              : setShowConfirmationModalOnContinue(false)
          }
          message={
            <Trans
              i18nKey={
                showConfirmationModalOnSkip
                  ? 'venueSetup.confirmMessageOnSkip'
                  : (() => {
                      const extraEmails =
                        emailSent[selectedVenueBranch?.value]?.filter(
                          (item) => item.email !== formData.email,
                        ) || [];
                      return extraEmails.length === 1
                        ? 'venueSetup.continueMessageSingle'
                        : 'venueSetup.continueMessageMultiple';
                    })()
              }
              values={
                showConfirmationModalOnContinue && {
                  userText: emailSent[selectedVenueBranch?.value]
                    ?.filter((item) => item.email !== formData.email)
                    ?.map((item) => item.email)
                    .join(', '),
                }
              }
            />
          }
        />
      )}
    </>
  );
};

export default ResetUniversalStaff;
